import { Dimmer, Icon, Input, Loader, Message } from 'semantic-ui-react';
import React, { useState } from 'react';

import Popup from '@src/components/common/Popup';
import { SpacedBetween } from '@src/components/common/SpacedStyle';
import RequestPromotion from '@src/services/Dashboard/PromoCodeSubmit';
import ContentContainer from '@modules/common/components/ContentContainer';
import PrimaryForm from '@modules/common/components/PrimaryForm';
import PrimaryButton from '@modules/common/components/buttons/PrimaryButton';
import { PrimaryBlankLink } from '@src/components/common/links';
import config from '@config/config';

type ClosableMessageProps = {
  children?: React.ReactNode;
  dismiss: () => void;
  type: 'SUCCESS' | 'WARNING';
};

const ClosableMessage: ({ children, dismiss, type }: ClosableMessageProps) => any = ({
  children,
  dismiss,
  type,
}: ClosableMessageProps) => {
  return (
    <Message
      icon
      onDismiss={() => dismiss()}
      positive={type === 'SUCCESS'}
      warning={type === 'WARNING'}
    >
      {children}
    </Message>
  );
};

const PromoCode = () => {
  const [promoCode, setPromoCode] = useState('');
  const [sendingPromoCode, setSendingPromoCode] = useState(false);

  const [successMessage, setSuccessMessage] = useState(false);
  const [warningMessage, setWarningMessage] = useState(false);

  const submitPromoCode = async () => {
    setSendingPromoCode(true);
    await RequestPromotion.postPromoCode({ code: promoCode })
      .then(_ => {
        setPromoCode('');
        setSendingPromoCode(false);
        setSuccessMessage(true);
        setWarningMessage(false);
      })
      .catch(_ => {
        setPromoCode('');
        setSendingPromoCode(false);
        setWarningMessage(true);
        setSuccessMessage(false);
      });
  };

  if (!config.showPromoCodeForm) {
    return null;
  }

  return (
    <Dimmer.Dimmable dimmed={sendingPromoCode}>
      <ContentContainer style={{ marginTop: '1rem', textAlign: 'left' }}>
        <Dimmer active={sendingPromoCode} inverted>
          <Loader />
        </Dimmer>
        <div className="content-container-header" style={{ width: '100%' }}>
          <SpacedBetween>
            <h3 style={{ marginBottom: '0' }}>Promotional code</h3>
            <Popup
              content={
                <>
                  To get the promotional code, register for and join us at our webinar!
                  <br />
                  <PrimaryBlankLink
                    href="https://www.sentinel-hub.com/explore/education/webinars/"
                    style={{ color: 'white' }}
                  >
                    Learn more ...
                  </PrimaryBlankLink>
                </>
              }
              hoverable
              trigger={<Icon name="info circle" />}
            />
          </SpacedBetween>
        </div>
        <PrimaryForm className="content">
          <Input
            action
            fluid
            onChange={(_, data) => {
              setPromoCode(data.value);
            }}
            placeholder="Type in a promotional code for bonus resources"
          >
            <input value={promoCode} />
            <PrimaryButton disabled={promoCode === ''} onClick={submitPromoCode} type="submit">
              Send
            </PrimaryButton>
          </Input>
        </PrimaryForm>

        {successMessage && (
          <ClosableMessage dismiss={() => setSuccessMessage(false)} type="SUCCESS">
            <Icon name="thumbs up" />
            We received the code. You can expect the reward in the following days. Thank you for
            using our services!
          </ClosableMessage>
        )}
        {warningMessage && (
          <ClosableMessage dismiss={() => setWarningMessage(false)} type="WARNING">
            <Icon name="thumbs down" />
            The code is either invalid or expired. Please try again with another one!
          </ClosableMessage>
        )}
      </ContentContainer>
    </Dimmer.Dimmable>
  );
};

export default PromoCode;
